import React, { useEffect, useState } from 'react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatNumber } from '../../utils/formatters';
import EmptyState from '../shared/EmptyState';
import { ColumnSelector, ColumnConfig } from '../ColumnSelector';
import './ActivityReportTable.css';

// Renders a count with thousands separators; zero values are muted so a
// row full of real activity doesn't get visually drowned out by "0"s.
const NumCell: React.FC<{ value?: number }> = ({ value }) => {
  const v = value ?? 0;
  return <td className={`art-num${v === 0 ? ' art-num-zero' : ''}`}>{formatNumber(v)}</td>;
};

interface ActivityRow {
  sort_key: string;
  period: string;
  user: string;
  tc_created?: number;
  tc_executed?: number;
  sc_created?: number;
  sc_executed?: number;
  runs_created?: number;
  runs_completed?: number;
  tc_ai?: number;
  tc_manual?: number;
  tc_ai_pct?: number;
  tc_manual_pct?: number;
  sc_ai?: number;
  sc_manual?: number;
  sc_ai_pct?: number;
  sc_manual_pct?: number;
}

type Period = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
type View = 'overall' | 'by_user' | 'ai_vs_manual';

// Each view has a completely different set of data columns, so the column
// selector's options (and what's persisted) switch along with the View dropdown.
const COLUMN_DEFS: Record<View, ColumnConfig[]> = {
  overall: [
    { key: 'period', label: 'Period', visible: true, required: true },
    { key: 'tc_created', label: 'TC', visible: true },
    { key: 'tc_executed', label: 'TC Executed', visible: true },
    { key: 'sc_created', label: 'TS', visible: true },
    { key: 'sc_executed', label: 'TS Executed', visible: true },
    { key: 'runs_created', label: 'Runs Created', visible: true },
    { key: 'runs_completed', label: 'Runs Completed', visible: true },
  ],
  by_user: [
    { key: 'period', label: 'Period', visible: true, required: true },
    { key: 'user', label: 'User', visible: true, required: true },
    { key: 'tc_created', label: 'TC', visible: true },
    { key: 'tc_executed', label: 'TC Executed', visible: true },
    { key: 'sc_created', label: 'TS', visible: true },
    { key: 'sc_executed', label: 'TS Executed', visible: true },
    { key: 'runs_created', label: 'Runs Created', visible: true },
    { key: 'runs_completed', label: 'Runs Completed', visible: true },
  ],
  ai_vs_manual: [
    { key: 'period', label: 'Period', visible: true, required: true },
    { key: 'user', label: 'User', visible: true, required: true },
    { key: 'tc_ai', label: 'TC (AI)', visible: true },
    { key: 'tc_ai_pct', label: 'TC AI %', visible: true },
    { key: 'tc_manual', label: 'TC (Manual)', visible: true },
    { key: 'tc_manual_pct', label: 'TC Manual %', visible: true },
    { key: 'sc_ai', label: 'TS (AI)', visible: true },
    { key: 'sc_ai_pct', label: 'TS AI %', visible: true },
    { key: 'sc_manual', label: 'TS (Manual)', visible: true },
    { key: 'sc_manual_pct', label: 'TS Manual %', visible: true },
  ],
};

export const ActivityReportTable: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [period, setPeriod] = useState<Period>('monthly');
  const [view, setView] = useState<View>('overall');
  const [monthsBack, setMonthsBack] = useState(12);
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [userFilter, setUserFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<string>>(new Set());
  const [columns, setColumns] = useState<ColumnConfig[]>(COLUMN_DEFS.overall);

  // Switch to the active view's column set, restoring any saved visibility
  // preference for that view from localStorage.
  useEffect(() => {
    const defaults = COLUMN_DEFS[view];
    const saved = localStorage.getItem(`art-columns-${view}`);
    if (saved) {
      try {
        const visibilityMap = JSON.parse(saved);
        setColumns(defaults.map(col => ({ ...col, visible: visibilityMap[col.key] ?? col.visible })));
        return;
      } catch (e) {
        // fall through to defaults
      }
    }
    setColumns(defaults);
  }, [view]);

  const isColVisible = (key: string) => columns.find(c => c.key === key)?.visible !== false;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient.get(`/api/v1/dashboard/activity-report?period=${period}&view=${view}&months_back=${monthsBack}`)
      .then(resp => {
        if (cancelled) return;
        const data = resp.data as any;
        setRows(data.rows || []);
        setUsers(data.users || []);
        setUserFilter('');
        setMonthFilter('');
      })
      .catch(() => { if (!cancelled) { setRows([]); setUsers([]); setError('Failed to load activity report.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, view, monthsBack]);

  // Collapsed-by-default for every period group except the most recent one
  // (the current month, when "Monthly" is selected) — reset whenever fresh
  // data loads, but not on the client-side user/month filters below.
  useEffect(() => {
    const periodKeys: string[] = [];
    rows.forEach(r => { if (!periodKeys.includes(r.sort_key)) periodKeys.push(r.sort_key); });
    if (periodKeys.length === 0) return;
    const mostRecent = periodKeys[periodKeys.length - 1];
    setCollapsedPeriods(new Set(periodKeys.filter(k => k !== mostRecent)));
  }, [rows]);

  const togglePeriodCollapse = (sortKey: string) => {
    setCollapsedPeriods(prev => {
      const next = new Set(prev);
      if (next.has(sortKey)) next.delete(sortKey);
      else next.add(sortKey);
      return next;
    });
  };

  // Distinct months present in the currently loaded range (Monthly period
  // only) — sorted newest first, deduped across users/AI-vs-Manual rows.
  const monthOptions = period === 'monthly'
    ? Array.from(new Map(rows.map(r => [r.sort_key, r.period])).entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
    : [];

  const handleExport = async () => {
    if (!isAdmin) return;
    setExporting(true);
    try {
      const authTokens = localStorage.getItem('auth_tokens');
      const tokens = authTokens ? JSON.parse(authTokens) : null;
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(
        `${apiBase}/api/v1/dashboard/export-report?period=${period}&view=${view}&months_back=${monthsBack}`,
        { headers: { Authorization: `Bearer ${tokens?.access_token}` } }
      );
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assurex_report_${view}_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const filtered = rows.filter(r =>
    (!userFilter || r.user === userFilter) &&
    (!monthFilter || r.sort_key === monthFilter)
  );

  // AI vs Manual — group by period, inject a bolded "Overall" summary row per period
  const renderAiVsManualRows = () => {
    const periodOrder: string[] = [];
    const byPeriod: Record<string, ActivityRow[]> = {};
    filtered.forEach(r => {
      if (!byPeriod[r.sort_key]) { byPeriod[r.sort_key] = []; periodOrder.push(r.sort_key); }
      byPeriod[r.sort_key].push(r);
    });

    const out: React.ReactNode[] = [];
    periodOrder.forEach(sk => {
      const group = byPeriod[sk];
      const totalTcAi = group.reduce((s, r) => s + (r.tc_ai ?? 0), 0);
      const totalTcManual = group.reduce((s, r) => s + (r.tc_manual ?? 0), 0);
      const totalScAi = group.reduce((s, r) => s + (r.sc_ai ?? 0), 0);
      const totalScManual = group.reduce((s, r) => s + (r.sc_manual ?? 0), 0);
      const tcTotal = totalTcAi + totalTcManual;
      const scTotal = totalScAi + totalScManual;
      const tcAiPct = tcTotal > 0 ? Math.round((totalTcAi / tcTotal) * 1000) / 10 : 0;
      const tcManualPct = tcTotal > 0 ? Math.round((totalTcManual / tcTotal) * 1000) / 10 : 0;
      const scAiPct = scTotal > 0 ? Math.round((totalScAi / scTotal) * 1000) / 10 : 0;
      const scManualPct = scTotal > 0 ? Math.round((totalScManual / scTotal) * 1000) / 10 : 0;
      const periodLabel = group[0].period;
      const isCollapsed = collapsedPeriods.has(sk);

      out.push(
        <tr
          key={`${sk}-summary`}
          className="art-row-summary art-row-collapsible"
          onClick={() => togglePeriodCollapse(sk)}
          title={isCollapsed ? 'Click to expand' : 'Click to collapse'}
        >
          {isColVisible('period') && (
            <td className="art-period">
              <span className={`art-collapse-chevron${isCollapsed ? '' : ' art-collapse-chevron-open'}`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </span>
              {periodLabel}
            </td>
          )}
          {view === 'ai_vs_manual' && isColVisible('user') && <td className="art-summary-label">Overall</td>}
          {isColVisible('tc_ai') && <td className="art-num art-summary-num">{formatNumber(totalTcAi)}</td>}
          {isColVisible('tc_ai_pct') && <td className="art-pct art-pct-ai"><span className="art-pct-badge">{tcAiPct}%</span></td>}
          {isColVisible('tc_manual') && <td className="art-num art-summary-num">{formatNumber(totalTcManual)}</td>}
          {isColVisible('tc_manual_pct') && <td className="art-pct art-pct-manual"><span className="art-pct-badge">{tcManualPct}%</span></td>}
          {isColVisible('sc_ai') && <td className="art-num art-summary-num">{formatNumber(totalScAi)}</td>}
          {isColVisible('sc_ai_pct') && <td className="art-pct art-pct-ai"><span className="art-pct-badge">{scAiPct}%</span></td>}
          {isColVisible('sc_manual') && <td className="art-num art-summary-num">{formatNumber(totalScManual)}</td>}
          {isColVisible('sc_manual_pct') && <td className="art-pct art-pct-manual"><span className="art-pct-badge">{scManualPct}%</span></td>}
        </tr>
      );

      if (!isCollapsed) {
        group.forEach((row, i) => {
          out.push(
            <tr key={`${sk}-${i}`}>
              {isColVisible('period') && <td className="art-period art-period-indent">{row.period}</td>}
              {isColVisible('user') && <td className="art-user">{row.user}</td>}
              {isColVisible('tc_ai') && <NumCell value={row.tc_ai} />}
              {isColVisible('tc_ai_pct') && <td className="art-pct art-pct-ai"><span className="art-pct-badge">{row.tc_ai_pct ?? 0}%</span></td>}
              {isColVisible('tc_manual') && <NumCell value={row.tc_manual} />}
              {isColVisible('tc_manual_pct') && <td className="art-pct art-pct-manual"><span className="art-pct-badge">{row.tc_manual_pct ?? 0}%</span></td>}
              {isColVisible('sc_ai') && <NumCell value={row.sc_ai} />}
              {isColVisible('sc_ai_pct') && <td className="art-pct art-pct-ai"><span className="art-pct-badge">{row.sc_ai_pct ?? 0}%</span></td>}
              {isColVisible('sc_manual') && <NumCell value={row.sc_manual} />}
              {isColVisible('sc_manual_pct') && <td className="art-pct art-pct-manual"><span className="art-pct-badge">{row.sc_manual_pct ?? 0}%</span></td>}
            </tr>
          );
        });
      }
    });
    return out;
  };

  return (
    <div className="art-card">
      <div className="art-header">
        <div className="art-header-title">
          <div className="art-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <div>
            <h3>Activity Report</h3>
            <p>Test case, scenario, and test run velocity over time</p>
          </div>
        </div>
        <div className="art-header-actions">
          <ColumnSelector
            columns={columns}
            onColumnsChange={setColumns}
            storageKey={`art-columns-${view}`}
          />
          <button
            className={`art-export-btn${!isAdmin ? ' art-export-disabled' : ''}`}
            onClick={handleExport}
            disabled={!isAdmin || exporting}
            title={isAdmin ? 'Export this report as CSV' : 'Only Admin users can export reports'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="art-controls">
        <div className="art-control-group">
          <label>Period</label>
          <select value={period} onChange={e => setPeriod(e.target.value as Period)} className="art-select">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div className="art-control-group">
          <label>View</label>
          <select value={view} onChange={e => setView(e.target.value as View)} className="art-select">
            <option value="overall">Overall</option>
            <option value="by_user">By User</option>
            <option value="ai_vs_manual">AI vs Manual</option>
          </select>
        </div>
        <div className="art-control-group">
          <label>Range</label>
          <select value={monthsBack} onChange={e => setMonthsBack(Number(e.target.value))} className="art-select">
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
          </select>
        </div>
        {period === 'monthly' && monthOptions.length > 0 && (
          <div className="art-control-group">
            <label>Month</label>
            <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="art-select">
              <option value="">All months</option>
              {monthOptions.map(([sortKey, label]) => (
                <option key={sortKey} value={sortKey}>{label}</option>
              ))}
            </select>
          </div>
        )}
        {(view === 'by_user' || view === 'ai_vs_manual') && users.length > 0 && (
          <div className="art-control-group">
            <label>User</label>
            <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="art-select">
              <option value="">All</option>
              {users.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        )}
      </div>

      {error && <div className="art-error">{error}</div>}

      {loading ? (
        <div className="art-skeleton" />
      ) : filtered.length === 0 ? (
        <EmptyState message="No activity data for this period." />
      ) : (
        <div className="art-table-wrap">
          <table className="art-table">
            <thead>
              <tr>
                {isColVisible('period') && <th>Period</th>}
                {(view === 'by_user' || view === 'ai_vs_manual') && isColVisible('user') && <th>User</th>}
                {view === 'ai_vs_manual' ? (
                  <>
                    {isColVisible('tc_ai') && <th className="art-th-num" title="Test Cases created by AI">TC (AI)</th>}
                    {isColVisible('tc_ai_pct') && <th className="art-th-num" title="Share of Test Cases created by AI">TC AI %</th>}
                    {isColVisible('tc_manual') && <th className="art-th-num" title="Test Cases created manually">TC (Manual)</th>}
                    {isColVisible('tc_manual_pct') && <th className="art-th-num" title="Share of Test Cases created manually">TC Manual %</th>}
                    {isColVisible('sc_ai') && <th className="art-th-num" title="Test Scenarios created by AI">TS (AI)</th>}
                    {isColVisible('sc_ai_pct') && <th className="art-th-num" title="Share of Test Scenarios created by AI">TS AI %</th>}
                    {isColVisible('sc_manual') && <th className="art-th-num" title="Test Scenarios created manually">TS (Manual)</th>}
                    {isColVisible('sc_manual_pct') && <th className="art-th-num" title="Share of Test Scenarios created manually">TS Manual %</th>}
                  </>
                ) : (
                  <>
                    {isColVisible('tc_created') && <th className="art-th-num" title="Test Cases created">TC</th>}
                    {isColVisible('tc_executed') && <th className="art-th-num" title="Test Cases executed">TC Executed</th>}
                    {isColVisible('sc_created') && <th className="art-th-num" title="Test Scenarios created">TS</th>}
                    {isColVisible('sc_executed') && <th className="art-th-num" title="Test Scenarios executed">TS Executed</th>}
                    {isColVisible('runs_created') && <th className="art-th-num" title="Test Runs created">Runs Created</th>}
                    {isColVisible('runs_completed') && <th className="art-th-num" title="Test Runs completed">Runs Completed</th>}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {view === 'ai_vs_manual' ? renderAiVsManualRows() : filtered.map((row, i) => (
                <tr key={i}>
                  {isColVisible('period') && <td className="art-period">{row.period}</td>}
                  {view === 'by_user' && isColVisible('user') && <td className="art-user">{row.user}</td>}
                  {isColVisible('tc_created') && <NumCell value={row.tc_created} />}
                  {isColVisible('tc_executed') && <NumCell value={row.tc_executed} />}
                  {isColVisible('sc_created') && <NumCell value={row.sc_created} />}
                  {isColVisible('sc_executed') && <NumCell value={row.sc_executed} />}
                  {isColVisible('runs_created') && <NumCell value={row.runs_created} />}
                  {isColVisible('runs_completed') && <NumCell value={row.runs_completed} />}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityReportTable;
